// Diagnoses a failed native HLS playback: fetches the playlist and its first variant with the
// browser's cookies and reports what the CDN answered, so a denied or unreachable source can be
// told apart from a media error.

const PROBE_TIMEOUT_MS = 5000;

type CdnDenial = 'missing-cookie' | 'rejected-cookie' | 'unknown';

export type ProbedRequest = {
  status?: number;
  denial?: CdnDenial;
  // Set instead of `status` when no response arrived: the fetch error's name.
  failure?: string;
};

export type HlsSourceProbe = {
  cookieEnabled: boolean;
  playlist: ProbedRequest;
  // Both absent when the playlist could not be loaded or lists no variants.
  variantUrl?: string;
  variant?: ProbedRequest;
};

// The CDN hands a request without a signed cookie on to the bucket, which answers with an XML
// AccessDenied; a cookie the CDN itself rejects gets Google's HTML 403 page instead.
const denialOf = (body: string): CdnDenial => {
  if (body.includes('<Code>AccessDenied</Code>')) return 'missing-cookie';
  if (body.includes('<title>403 Forbidden</title>')) return 'rejected-cookie';
  return 'unknown';
};

const request = async (
  url: string
): Promise<{ result: ProbedRequest; body?: string }> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      credentials: 'include',
      cache: 'no-store',
      signal: controller.signal,
    });
    const body = await response.text();
    if (response.status === 403) {
      return { result: { status: 403, denial: denialOf(body) } };
    }
    return {
      result: { status: response.status },
      body: response.ok ? body : undefined,
    };
  } catch (error) {
    return {
      result: { failure: error instanceof Error ? error.name : 'Unknown' },
    };
  } finally {
    clearTimeout(timeout);
  }
};

const firstVariantUrl = (playlist: string, playlistUrl: string) => {
  const lines = playlist.split(/\r?\n/).map((line) => line.trim());
  const streamInfIndex = lines.findIndex((line) =>
    line.startsWith('#EXT-X-STREAM-INF')
  );
  if (streamInfIndex === -1) return undefined;
  const uri = lines
    .slice(streamInfIndex + 1)
    .find((line) => line !== '' && !line.startsWith('#'));
  return uri ? new URL(uri, playlistUrl).href : undefined;
};

/**
 * Requests an HLS playlist and its first variant with the browser's cookies, as the native player
 * does, and reports what the CDN answered. Native HLS playback only exposes a bare MediaError, so
 * this is the only way to learn why the media could not be loaded.
 *
 * Never rejects: a request that gets no response within 5 s is reported as a `failure`.
 */
export const probeHlsSource = async (
  playlistUrl: string
): Promise<HlsSourceProbe> => {
  const cookieEnabled = navigator.cookieEnabled;

  const playlist = await request(playlistUrl);
  const variantUrl =
    playlist.body === undefined
      ? undefined
      : firstVariantUrl(playlist.body, playlistUrl);
  if (!variantUrl) {
    return { cookieEnabled, playlist: playlist.result };
  }

  const variant = await request(variantUrl);
  return {
    cookieEnabled,
    playlist: playlist.result,
    variantUrl,
    variant: variant.result,
  };
};
